import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { ArrowLeft, Lock, Mail, User as UserIcon, Eye, EyeOff, LogOut, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import MessagePreferences from "@/components/MessagePreferences";
import AppFooter from "@/components/AppFooter";
import InstallAppBanner from "@/components/InstallAppBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useContext(AuthContext);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  // Whether the app is currently running as an installed PWA. Used to hide
  // the "Install on your phone" card in Settings — there's no point asking
  // someone to install something they're already inside of.
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  // Re-show the install prompt: clear the dismiss flag the banner sets when
  // a user closes it, then dispatch the imperative event the InstallAppBanner
  // listens for so the install flow opens immediately without a page reload.
  const handleReshowInstall = () => {
    try { localStorage.removeItem("pwa-install-dismissed"); } catch (e) { /* private mode */ }
    window.dispatchEvent(new Event("pwa-show-install-guide"));
  };

  // The "password section" appears for every authenticated user, but adapts
  // its copy based on whether the user already has a local password:
  //   has_password = true  → "Change password" (existing flow)
  //   has_password = false → "Add a backup password" (intentional path for
  //     Google users to add an alternate sign-in method while authenticated
  //     on their Google session — replaces the silent /register merge that
  //     was an account-takeover risk).
  // We always show the section so Google users have a discoverable way to
  // strengthen their account, and we drive copy/labels off `has_password`
  // rather than `auth_type` (which is unreliable for admin-pre-created users).
  const hasPassword = !!user.has_password;
  const signInLabel = hasPassword ? "Email & password" : "Google";

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/\d/.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(hasPassword ? "Password updated" : "Backup password added — you can now sign in with email + password.");
      setNewPassword("");
      setConfirmPassword("");
      // Refresh user state so has_password flips and the card morphs from
      // "Add a backup password" → "Change password" without a manual reload.
      if (!hasPassword) checkAuth().catch(() => { /* non-fatal */ });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-app flex flex-col">
      <InstallAppBanner />
      <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto max-w-3xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo variant="icon" size={28} />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="ge-btn-ghost text-[13px] gap-1"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
        </div>
      </header>

      <main className="flex-1 container mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10">
        <section className="mb-8">
          <p className="ge-eyebrow">Your settings</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink mt-1.5">Account &amp; preferences</h1>
          <p className="ge-page-sub mt-1.5">Manage how you sign in, what alerts you get, and your account info.</p>
        </section>

        {/* Account info */}
        <section className="ge-section mb-6" data-testid="account-info-card">
          <div className="ge-section-head">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-accent" />
              <h2 className="ge-section-title">Account</h2>
            </div>
          </div>
          <div className="px-5 py-4 space-y-3">
            <Row label="Name" value={user.name} />
            <Row label="Email" value={user.email} icon={<Mail className="w-3.5 h-3.5 text-ink-muted" />} />
            <Row
              label="Sign-in method"
              value={signInLabel}
              icon={<ShieldCheck className="w-3.5 h-3.5 text-ink-muted" />}
            />
          </div>
        </section>

        {/* Password section — copy adapts to whether the user already has
            a local password (change) or is a Google user adding their first
            backup password (add). */}
        <section className="ge-section mb-6" data-testid="password-card">
          <div className="ge-section-head">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-accent" />
              <h2 className="ge-section-title">
                {hasPassword ? "Change password" : "Add a backup password"}
              </h2>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className="px-5 py-4 space-y-4">
            {!hasPassword && (
              <p className="text-[13px] text-ink-muted leading-relaxed">
                You currently sign in with Google. Adding a backup password lets you
                sign in with email + password as well — useful if you're ever locked
                out of Google. This <strong>does not</strong> change your Google account password.
              </p>
            )}
            <div>
              <Label htmlFor="new-pwd">New password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-ink-soft" />
                <Input
                  id="new-pwd"
                  data-testid="new-password-input"
                  type={showNewPwd ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd((v) => !v)}
                  className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                  aria-label="toggle password visibility"
                >
                  {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[12px] text-ink-muted mt-1.5">At least 8 characters, 1 uppercase, 1 number</p>
            </div>
            <div>
              <Label htmlFor="confirm-pwd">Confirm new password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-ink-soft" />
                <Input
                  id="confirm-pwd"
                  data-testid="confirm-password-input"
                  type={showConfirmPwd ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd((v) => !v)}
                  className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                  aria-label="toggle password visibility"
                >
                  {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting || !newPassword || !confirmPassword}
              className="bg-ink hover:bg-ink-hover"
              data-testid="save-password-btn"
            >
              {submitting
                ? "Saving…"
                : hasPassword
                  ? "Update password"
                  : "Add backup password"}
            </Button>
          </form>
        </section>

        {/* Notification preferences (existing component embedded) */}
        <section className="ge-section mb-6" data-testid="notifications-card">
          <MessagePreferences />
        </section>

        {/* Re-show install prompt — recovers users who dismissed the PWA
            install banner before they understood what it offered. Clears
            the localStorage dismiss flag AND dispatches the imperative
            event so the install guide opens immediately. Hidden when the
            app is already running as an installed PWA — no point re-
            prompting in that case. */}
        {!isStandalone && (
          <section className="ge-section mb-6" data-testid="install-app-card">
            <button
              onClick={handleReshowInstall}
              data-testid="settings-reshow-install-btn"
              className="w-full px-5 py-4 flex items-center gap-3 hover:bg-app transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-lg bg-accent-tint text-accent grid place-items-center flex-shrink-0 border border-accent/30">
                <Smartphone className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-[14px] font-semibold tracking-tight text-ink">Install on your phone</p>
                <p className="text-[12px] text-ink-muted mt-0.5">
                  One-tap score entry, full-screen, works offline-friendly.
                </p>
              </div>
            </button>
          </section>
        )}

        {/* Sign out */}
        <section className="ge-section mb-6">
          <button
            onClick={handleSignOut}
            data-testid="settings-signout-btn"
            className="w-full px-5 py-4 flex items-center gap-3 hover:bg-app transition-colors text-left"
          >
            <div className="w-9 h-9 rounded-lg bg-app text-ink-muted grid place-items-center flex-shrink-0 border border-line">
              <LogOut className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-semibold tracking-tight text-ink">Sign out</p>
              <p className="text-[12px] text-ink-muted mt-0.5">End your session on this device</p>
            </div>
          </button>
        </section>
      </main>

      <AppFooter />
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="text-[12.5px] font-semibold uppercase tracking-[0.06em] text-ink-muted flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className="text-[14px] text-ink font-medium text-right break-all">{value || "—"}</div>
    </div>
  );
}
